import { Component } from "@theme/component";
import { MediaStartedPlayingEvent } from "@theme/events";

const YOUTUBE_EMBED_ORIGIN = "https://www.youtube-nocookie.com";
const YOUTUBE_API_URL = "https://www.youtube.com/iframe_api";
const DEFAULT_VOLUME = 50;
const PLAYING_STATE = 1;

/**
 * @typedef {Object} YouTubePlayer
 * @property {(volume: number) => void} setVolume
 * @property {(module: string, option: string, value: unknown) => void} setOption
 * @property {() => void} playVideo
 * @property {() => void} destroy
 */

/**
 * @typedef {Object} YouTubePlayerEvent
 * @property {YouTubePlayer} target
 * @property {number} [data]
 */

/**
 * @typedef {Object} YouTubePlayerOptions
 * @property {Object} events
 * @property {(event: YouTubePlayerEvent) => void} events.onReady
 * @property {(event: YouTubePlayerEvent) => void} events.onStateChange
 * @property {(event: YouTubePlayerEvent) => void} events.onApiChange
 */

/** @typedef {new (iframe: HTMLIFrameElement, options: YouTubePlayerOptions) => YouTubePlayer} YouTubePlayerConstructor */

/**
 * @typedef {Object} YouTubeApi
 * @property {YouTubePlayerConstructor} Player
 * @property {(callback: () => void) => void} ready
 */

/** @type {Promise<YouTubeApi> | null} */
let youtubeApiPromise = null;

/** @returns {Promise<YouTubeApi>} */
const loadYouTubeApi = () => {
  const scope = /** @type {typeof globalThis & { YT?: YouTubeApi }} */ (globalThis);
  if (scope.YT?.Player) {
    return Promise.resolve(scope.YT);
  }
  if (youtubeApiPromise !== null) {
    return youtubeApiPromise;
  }

  const pendingApi = new Promise((resolve, reject) => {
    const resolveWhenReady = () => {
      const api = scope.YT;
      if (!api?.ready) {
        reject(new Error("YouTube iframe API did not initialize"));
        return;
      }

      api.ready(() => {
        if (scope.YT?.Player) {
          resolve(scope.YT);
        } else {
          reject(new Error("YouTube player API is unavailable"));
        }
      });
    };

    const existingScript = document.querySelector(`script[src="${YOUTUBE_API_URL}"]`);
    const script = existingScript instanceof HTMLScriptElement ? existingScript : document.createElement("script");
    if (!(existingScript instanceof HTMLScriptElement)) {
      script.src = YOUTUBE_API_URL;
      script.async = true;
      script.fetchPriority = "low";
      script.referrerPolicy = "strict-origin-when-cross-origin";
      document.head.append(script);
    }

    if (scope.YT?.ready) {
      resolveWhenReady();
      return;
    }

    script.addEventListener("load", resolveWhenReady, { once: true });
    script.addEventListener("error", () => reject(new Error("YouTube iframe API failed to load")), { once: true });
  });

  youtubeApiPromise = pendingApi.catch(error => {
    youtubeApiPromise = null;
    throw error;
  });

  return youtubeApiPromise;
};

/**
 * @typedef {Object} VideoFacadeRefs
 * @property {HTMLButtonElement} playButton - Poster play button
 * @property {HTMLTemplateElement} embedTemplate - Template holding the iframe embed
 * @property {HTMLElement} frame - The 16:9 poster frame
 * @property {HTMLDialogElement} [lightbox] - Modal player (lightbox mode only)
 * @property {HTMLElement} [lightboxFrame] - Iframe target inside the dialog
 * @property {HTMLButtonElement} [closeButton] - Dialog close button
 */

/**
 * Video facade: poster → YouTube embed, template-clone only (no HTML string injection).
 * Inline mode swaps in place once. Lightbox mode
 * uses native <dialog>.showModal() semantics (Escape, focus trap, focus return); closing
 * removes the iframe so playback stops.
 *
 * @extends {Component<VideoFacadeRefs>}
 */
class VideoFacadeComponent extends Component {
  requiredRefs = ["playButton", "embedTemplate", "frame"];

  /** @type {AbortController} */
  #controller = new AbortController();

  /** @type {YouTubePlayer | null} */
  #player = null;

  #onLightboxClose = () => {
    this.#player?.destroy();
    this.#player = null;
    this.refs.lightboxFrame?.replaceChildren();
  };

  /**
   * The film already contains designed, burned-in captions. Keep YouTube's
   * separate caption track off so viewers do not see two caption layers.
   * @param {YouTubePlayer} player
   */
  #disableCaptions = player => {
    player.setOption("captions", "track", {});
  };

  /** @param {YouTubePlayerEvent} event */
  #onPlayerReady = event => {
    event.target.setVolume(DEFAULT_VOLUME);
    this.#disableCaptions(event.target);
    event.target.playVideo();
  };

  /** @param {YouTubePlayerEvent} event */
  #onPlayerStateChange = event => {
    if (event.data === PLAYING_STATE) {
      this.#disableCaptions(event.target);
    }
  };

  /** @param {YouTubePlayerEvent} event */
  #onPlayerApiChange = event => {
    this.#disableCaptions(event.target);
  };

  /**
   * Apply player defaults once the deferred iframe is ready to receive API commands.
   * @param {Event} event
   */
  #onPlayerLoad = event => {
    const iframe = event.currentTarget;
    if (!(iframe instanceof HTMLIFrameElement) || !iframe.isConnected) {
      return;
    }

    const commands = [
      { event: "command", func: "setVolume", args: [DEFAULT_VOLUME] },
      { event: "command", func: "setOption", args: ["captions", "track", {}] },
      { event: "command", func: "playVideo", args: "" },
    ];

    for (const command of commands) {
      iframe.contentWindow?.postMessage(JSON.stringify(command), YOUTUBE_EMBED_ORIGIN);
    }
  };

  /**
   * Click-outside close: clicks on the backdrop land on the dialog element
   * itself — every content click targets a descendant instead.
   * @param {MouseEvent} event
   */
  #onLightboxClick = event => {
    if (event.target === this.refs.lightbox) {
      this.close();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    // Stop playback whenever the dialog closes (Escape, backdrop, close button).
    this.refs.lightbox?.addEventListener("close", this.#onLightboxClose, { signal: this.#controller.signal });
    this.refs.lightbox?.addEventListener("click", this.#onLightboxClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    this.#player?.destroy();
    this.#player = null;
  }

  /** @param {HTMLIFrameElement} iframe */
  async #initializePlayer(iframe) {
    try {
      const api = await loadYouTubeApi();
      if (!iframe.isConnected) {
        return;
      }

      this.#player = new api.Player(iframe, {
        events: {
          onReady: this.#onPlayerReady,
          onStateChange: this.#onPlayerStateChange,
          onApiChange: this.#onPlayerApiChange,
        },
      });
    } catch {
      // The load-time postMessage commands keep the player usable when the helper API is blocked.
    }
  }

  /**
   * Clone and mount the deferred player, wiring defaults before it begins loading.
   * @param {HTMLElement} target
   * @returns {HTMLIFrameElement | null}
   */
  #mountPlayer(target) {
    const embed = this.refs.embedTemplate.content.cloneNode(true);
    if (!(embed instanceof DocumentFragment)) {
      return null;
    }

    const iframe = embed.querySelector("iframe");
    if (iframe) {
      iframe.addEventListener("load", this.#onPlayerLoad, {
        once: true,
        signal: this.#controller.signal,
      });

      const source = new URL(iframe.src);
      if (source.origin === YOUTUBE_EMBED_ORIGIN) {
        source.searchParams.set("origin", globalThis.location.origin);
        iframe.src = source.toString();
      }
    }
    target.append(embed);
    if (iframe) {
      this.#initializePlayer(iframe);
    }

    return iframe;
  }

  play() {
    if (this.dataset.mode === "lightbox" && this.refs.lightbox && this.refs.lightboxFrame) {
      this.refs.lightbox.showModal();
      this.#mountPlayer(this.refs.lightboxFrame);
    } else {
      this.refs.playButton.hidden = true;
      const iframe = this.#mountPlayer(this.refs.frame);
      iframe?.focus();
    }
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
  }

  close() {
    this.refs.lightbox?.close();
  }
}

if (!customElements.get("video-facade-component")) {
  customElements.define("video-facade-component", VideoFacadeComponent);
}

export { VideoFacadeComponent };
